/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './CourseDetails.css';
import axios from 'axios';
import html2canvas from 'html2canvas';
import { API_BASE_URL } from '../config';
import { API_ENDPOINTS } from '../config/api';

// Türkçe karakterleri İngilizce karakterlere çevir
const turkishToEnglish = (text) => {
  if (!text) return '';

  const turkishChars = {
    'ç': 'c', 'Ç': 'C',
    'ğ': 'g', 'Ğ': 'G',
    'ı': 'i', 'İ': 'I',
    'ö': 'o', 'Ö': 'O',
    'ş': 's', 'Ş': 'S',
    'ü': 'u', 'Ü': 'U'
  };

  return text.replace(/[çÇğĞıİöÖşŞüÜ]/g, letter => turkishChars[letter] || letter);
};

const registerOpenSansFont = (doc, base64) => {
  if (!base64) {
    console.error('OpenSans font base64 verisi boş!');
    return false;
  }
  try {
    // Font dosyasını VFS'e ekle
    doc.addFileToVFS('OpenSans-Regular.ttf', base64);

    // Font'u farklı stillerde kaydet
    doc.addFont('OpenSans-Regular.ttf', 'OpenSans', 'normal');
    doc.addFont('OpenSans-Regular.ttf', 'OpenSans', 'bold');
    doc.addFont('OpenSans-Regular.ttf', 'OpenSans', 'italic');
    doc.addFont('OpenSans-Regular.ttf', 'OpenSans', 'bolditalic');

    // Font yükleme testi - Türkçe karakterlerle test et
    try {
      doc.setFont('OpenSans', 'normal');
      doc.setFontSize(10);
      // Türkçe karakterleri test et (görünmez yerde)
      const testText = 'İğüşöçÇĞŞÜ';
      try {
        doc.text(testText, -100, -100);
        console.log('OpenSans fontu başarıyla yüklendi ve Türkçe karakterler destekleniyor');
        return true;
      } catch (testErr) {
        console.warn('Font yüklendi ancak Türkçe karakter testi başarısız:', testErr);
        // Yine de true döndür çünkü font yüklendi
        return true;
      }
    } catch (e) {
      console.error('OpenSans fontu yüklendi ama kullanılamıyor:', e);
      return false;
    }
  } catch (err) {
    console.error('OpenSans fontu kaydedilemedi:', err);
    return false;
  }
};

const loadOpenSansFont = async (doc) => {
  console.log('OpenSans fontu yükleniyor...');

  // Önce API'den dene
  try {
    console.log('API\'den font yükleniyor...');
    const res = await axios.get(API_ENDPOINTS.FONT_OPENSANS);
    const base64 = res?.data?.base64;
    if (base64 && typeof base64 === 'string' && base64.length > 100) {
      console.log('API\'den font alındı, uzunluk:', base64.length);
      const result = registerOpenSansFont(doc, base64);
      if (result) {
        console.log('OpenSans fontu API\'den başarıyla yüklendi');
        return true;
      }
    } else {
      console.warn('API\'den gelen base64 verisi geçersiz');
    }
  } catch (err) {
    console.warn('OpenSans API yüklenemedi, public klasörü denenecek:', err?.message || err);
  }

  // Public klasörden dene
  try {
    console.log('Public klasörden font yükleniyor...');
    const resp = await fetch('/OpenSans-Regular.ttf');
    if (resp.ok) {
      const blob = await resp.blob();
      console.log('Font blob alındı, boyut:', blob.size);
      const arrayBuffer = await blob.arrayBuffer();
      const bytes = new Uint8Array(arrayBuffer);
      let binary = '';
      for (let i = 0; i < bytes.byteLength; i += 1) {
        binary += String.fromCharCode(bytes[i]);
      }
      const base64FromTtf = btoa(binary);
      console.log('Font base64\'e çevrildi, uzunluk:', base64FromTtf.length);
      const result = registerOpenSansFont(doc, base64FromTtf);
      if (result) {
        console.log('OpenSans fontu public klasörden başarıyla yüklendi');
        return true;
      }
    } else {
      console.warn('Public klasörden font dosyası bulunamadı, status:', resp.status);
    }
  } catch (err) {
    console.error('OpenSans font public klasörden yüklenemedi:', err);
  }

  // Google Fonts CDN'den dene
  try {
    console.log('Google Fonts CDN\'den font yükleniyor...');
    // OpenSans Regular fontunu Google Fonts'tan yükle
    const fontUrl = 'https://fonts.gstatic.com/s/opensans/v34/memSYaGs126MiZpBA-UvWbX2vVnXBbObj2OVZyOOSr4dVJWUgsjZ0B4gaVc.woff2';
    const resp = await fetch(fontUrl);
    if (resp.ok) {
      const arrayBuffer = await resp.arrayBuffer();
      const bytes = new Uint8Array(arrayBuffer);
      let binary = '';
      for (let i = 0; i < bytes.byteLength; i += 1) {
        binary += String.fromCharCode(bytes[i]);
      }
      const base64FromWoff2 = btoa(binary);
      console.log('Google Fonts\'tan font alındı, base64 uzunluk:', base64FromWoff2.length);
      // WOFF2 formatını TTF'ye çeviremeyiz, bu yüzden bu yöntem çalışmayabilir
      // Alternatif: jsPDF'in kendi font desteğini kullan
    }
  } catch (err) {
    console.error('Google Fonts\'tan font yüklenemedi:', err);
  }

  console.warn('OpenSans fontu hiçbir kaynaktan yüklenemedi! Türkçe karakterler korunacak ancak bazı fontlar desteklemeyebilir.');
  return false;
};

const CourseDetails = ({ programId, courseCode, userType, electiveLessonId, isElectiveLesson }) => {
  console.log('CourseDetails component rendered with:', { programId, courseCode, electiveLessonId, isElectiveLesson });
  const navigate = useNavigate();
  const [courseDetails, setCourseDetails] = useState(null);
  const [courseResources, setCourseResources] = useState(null);
  const [learningOutcomes, setLearningOutcomes] = useState([]);
  const [weeklyTopics, setWeeklyTopics] = useState([]);
  const [weekNotes, setWeekNotes] = useState({});
  const [weekVideos, setWeekVideos] = useState({});
  const [videoInputs, setVideoInputs] = useState({});
  const [videoLoadingState, setVideoLoadingState] = useState({});
  const [isWeekModalOpen, setIsWeekModalOpen] = useState(false);
  const [selectedWeekNumber, setSelectedWeekNumber] = useState(null);
  const [programOutcomes, setProgramOutcomes] = useState([]);
  const [aktsActivities, setAktsActivities] = useState([]);
  const [sustainableDevelopmentGoals, setSustainableDevelopmentGoals] = useState([]);
  const [evaluationSystem, setEvaluationSystem] = useState([]);
  const [teachingStaff, setTeachingStaff] = useState([]);
  const [programLearningOutcomes, setProgramLearningOutcomes] = useState([]);
  const [learningProgramMapping, setLearningProgramMapping] = useState([]);
  const [outcomesMatrix, setOutcomesMatrix] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const pageRef = useRef(null);

  useEffect(() => {
    if (programId && (courseCode || (isElectiveLesson && electiveLessonId))) {
      fetchCourseDetails();
    }
  }, [programId, courseCode, electiveLessonId, isElectiveLesson]);

  const fetchCourseDetails = useCallback(async () => {
    try {
      setLoading(true);
      console.log('Fetching course details for:', programId, courseCode, electiveLessonId, isElectiveLesson);

      const baseEndpoint = `${API_BASE_URL}/api/programs`;

      // Elective lesson ise farklı endpoint kullan
      let courseDetailsRes;
      if (isElectiveLesson && electiveLessonId) {
        const lessonId = parseInt(electiveLessonId);
        if (isNaN(lessonId)) {
          throw new Error('Invalid elective lesson ID');
        }
        courseDetailsRes = await fetch(`${baseEndpoint}/${programId}/elective-lessons/${lessonId}/course-details`);
      } else {
        courseDetailsRes = await fetch(`${baseEndpoint}/${programId}/courses/${courseCode}/course-details`);
      }

      // Elective lesson için tüm endpoint'ler elective-lessons endpoint'lerini kullanır
      const lessonId = isElectiveLesson && electiveLessonId ? parseInt(electiveLessonId) : null;
      const [courseResourcesRes, learningOutcomesRes, weeklyTopicsRes, programOutcomesRes, aktsActivitiesRes, sustainableDevelopmentGoalsRes, evaluationSystemRes, teachingStaffRes, programLearningOutcomesRes, outcomesMatrixRes] = await Promise.all([
        isElectiveLesson && lessonId
          ? fetch(`${baseEndpoint}/${programId}/elective-lessons/${lessonId}/course-resources`)
          : fetch(`${baseEndpoint}/${programId}/courses/${courseCode}/course-resources`),
        isElectiveLesson && lessonId
          ? fetch(`${baseEndpoint}/${programId}/elective-lessons/${lessonId}/learning-outcomes`)
          : fetch(`${baseEndpoint}/${programId}/courses/${courseCode}/learning-outcomes`),
        isElectiveLesson && lessonId
          ? fetch(`${baseEndpoint}/${programId}/elective-lessons/${lessonId}/weekly-topics`)
          : fetch(`${baseEndpoint}/${programId}/courses/${courseCode}/weekly-topics`),
        isElectiveLesson && lessonId
          ? fetch(`${baseEndpoint}/${programId}/elective-lessons/${lessonId}/program-outcomes`)
          : fetch(`${baseEndpoint}/${programId}/courses/${courseCode}/program-outcomes`),
        isElectiveLesson && lessonId
          ? fetch(`${baseEndpoint}/${programId}/elective-lessons/${lessonId}/akts-activities`)
          : fetch(`${baseEndpoint}/${programId}/courses/${courseCode}/akts-activities`),
        isElectiveLesson && lessonId
          ? fetch(`${baseEndpoint}/${programId}/elective-lessons/${lessonId}/sustainable-development-goals`)
          : fetch(`${baseEndpoint}/${programId}/courses/${courseCode}/sustainable-development-goals`),
        isElectiveLesson && lessonId
          ? fetch(`${baseEndpoint}/${programId}/elective-lessons/${lessonId}/evaluation-system`)
          : fetch(`${baseEndpoint}/${programId}/courses/${courseCode}/evaluation-system`),
        isElectiveLesson && lessonId
          ? fetch(`${baseEndpoint}/${programId}/elective-lessons/${lessonId}/teaching-staff`)
          : fetch(`${baseEndpoint}/${programId}/courses/${courseCode}/teaching-staff`),
        isElectiveLesson && lessonId
          ? fetch(`${baseEndpoint}/${programId}/elective-lessons/${lessonId}/program-outcomes`)
          : fetch(`${baseEndpoint}/${programId}/courses/${courseCode}/program-outcomes`),
        isElectiveLesson && lessonId
          ? fetch(`${baseEndpoint}/${programId}/elective-lessons/${lessonId}/learning-program-outcomes-matrix`)
          : fetch(`${baseEndpoint}/${programId}/courses/${courseCode}/learning-program-outcomes-matrix`)
      ]);

      console.log('Response statuses:', {
        courseDetails: courseDetailsRes.status,
        courseResources: courseResourcesRes.status,
        learningOutcomes: learningOutcomesRes.status,
        weeklyTopics: weeklyTopicsRes.status,
        programOutcomes: programOutcomesRes.status
      });

      if (!courseDetailsRes.ok) {
        throw new Error('Course details yüklenemedi');
      }

      const courseDetailsData = await courseDetailsRes.json();
      const courseResourcesData = courseResourcesRes.ok ? await courseResourcesRes.json() : { ders_kaynaklari: [], onerilen_kaynaklar: [] };
      const learningOutcomesData = learningOutcomesRes.ok ? await learningOutcomesRes.json() : [];
      const weeklyTopicsData = weeklyTopicsRes.ok ? await weeklyTopicsRes.json() : [];
      const programOutcomesData = programOutcomesRes.ok ? await programOutcomesRes.json() : [];
      const aktsActivitiesData = aktsActivitiesRes.ok ? await aktsActivitiesRes.json() : [];
      const sustainableDevelopmentGoalsData = sustainableDevelopmentGoalsRes.ok ? await sustainableDevelopmentGoalsRes.json() : [];
      const evaluationSystemData = evaluationSystemRes.ok ? await evaluationSystemRes.json() : [];
      const teachingStaffData = teachingStaffRes.ok ? await teachingStaffRes.json() : [];
      const programLearningOutcomesData = programLearningOutcomesRes.ok ? await programLearningOutcomesRes.json() : [];
      const outcomesMatrixData = outcomesMatrixRes.ok ? await outcomesMatrixRes.json() : null;

      console.log('Course details data:', courseDetailsData);
      console.log('Course resources data:', courseResourcesData);
      console.log('Learning outcomes data:', learningOutcomesData);
      console.log('Weekly topics data:', weeklyTopicsData);
      console.log('Program outcomes data:', programOutcomesData);
      console.log('AKTS activities data:', aktsActivitiesData);
      console.log('Sustainable development goals data:', sustainableDevelopmentGoalsData);
      console.log('Evaluation system data:', evaluationSystemData);
      console.log('Teaching staff data:', teachingStaffData);
      console.log('Program learning outcomes data:', programLearningOutcomesData);

      setCourseDetails(courseDetailsData);
      setCourseResources(courseResourcesData);
      setLearningOutcomes(learningOutcomesData);
      setWeeklyTopics(weeklyTopicsData);
      setProgramOutcomes(programOutcomesData);
      setAktsActivities(aktsActivitiesData);
      setSustainableDevelopmentGoals(sustainableDevelopmentGoalsData);
      setEvaluationSystem(evaluationSystemData);
      setTeachingStaff(teachingStaffData);
      setProgramLearningOutcomes(programLearningOutcomesData);
      setOutcomesMatrix(outcomesMatrixData);

      // Debug: Matris verisini konsola yazdır
      if (outcomesMatrixData) {
        console.log('=== Outcomes Matrix Debug ===');
        console.log('Outcomes Matrix Data:', outcomesMatrixData);
        console.log('Program Outcomes:', outcomesMatrixData.program_outcomes);
        console.log('Matrix Rows:', outcomesMatrixData.matrix);
        if (outcomesMatrixData.matrix && outcomesMatrixData.matrix.length > 0) {
          console.log('First row:', outcomesMatrixData.matrix[0]);
          console.log('First row contributions:', outcomesMatrixData.matrix[0].contributions);
        }
      } else {
        console.log('Outcomes Matrix Data is null or undefined');
      }

      // Debug: Matris verisini konsola yazdır
      if (outcomesMatrixData) {
        console.log('Outcomes Matrix Data:', outcomesMatrixData);
        console.log('Program Outcomes:', outcomesMatrixData.program_outcomes);
        console.log('Matrix Rows:', outcomesMatrixData.matrix);
        if (outcomesMatrixData.matrix && outcomesMatrixData.matrix.length > 0) {
          console.log('First row contributions:', outcomesMatrixData.matrix[0].contributions);
        }
      }
    } catch (err) {
      console.error('Error fetching course details:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [programId, courseCode, electiveLessonId, isElectiveLesson, userType]);

  const parseWeekValue = (value) => {
    if (value === null || value === undefined) return 0;
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
      const m = value.match(/(\d+)/);
      return m ? parseInt(m[1], 10) : 0;
    }
    if (!Number.isNaN(Number(value))) {
      return Number(value);
    }
    return 0;
  };

  const fetchWeeklyNotes = useCallback(async () => {
    // Seçmeli dersler için de weekly-notes endpoint'i var
    try {
      const lessonId = isElectiveLesson && electiveLessonId ? parseInt(electiveLessonId) : null;
      const url = isElectiveLesson && lessonId
        ? `${API_BASE_URL}/api/programs/${programId}/elective-lessons/${lessonId}/weekly-notes`
        : `${API_BASE_URL}/api/programs/${programId}/courses/${courseCode}/weekly-notes`;
      const res = await fetch(url, {
        headers: {
          'User-Type': userType || 'guest'
        }
      });
      if (!res.ok) {
        // Endpoint olmayabilir; sessiz geç
        return;
      }
      const list = await res.json();
      // Beklenen: [{ id, week_number | week, name }]
      const grouped = {};
      (list || []).forEach(item => {
        const rawWeek = item.week_number || item.week || item.weekName || '';
        const weekNum = parseWeekValue(rawWeek);
        if (!grouped[weekNum]) grouped[weekNum] = [];
        grouped[weekNum].push({
          id: item.id,
          name: item.name || item.file_name || 'Dosya'
        });
      });
      setWeekNotes(grouped);
    } catch (e) {
      console.warn('Haftalık notlar çekilemedi:', e);
    }
  }, [programId, courseCode, userType, isElectiveLesson, electiveLessonId]);

  const fetchWeeklyVideos = useCallback(async () => {
    // Seçmeli dersler için de weekly-videos endpoint'i var
    try {
      const lessonId = isElectiveLesson && electiveLessonId ? parseInt(electiveLessonId) : null;
      const url = isElectiveLesson && lessonId
        ? `${API_BASE_URL}/api/programs/${programId}/elective-lessons/${lessonId}/weekly-videos`
        : `${API_BASE_URL}/api/programs/${programId}/courses/${courseCode}/weekly-videos`;
      const res = await fetch(url, {
        headers: {
          'User-Type': userType || 'guest'
        }
      });
      if (!res.ok) {
        return;
      }
      const list = await res.json();
      const grouped = {};
      (list || []).forEach(item => {
        const weekNum = parseWeekValue(item.week_number || item.week);
        if (!grouped[weekNum]) grouped[weekNum] = [];
        grouped[weekNum].push({
          id: item.id,
          title: item.title,
          url: item.url
        });
      });
      setWeekVideos(grouped);
    } catch (e) {
      console.warn('Haftalık video linkleri çekilemedi:', e);
    }
  }, [programId, courseCode, userType, isElectiveLesson, electiveLessonId]);

  useEffect(() => {
    if (programId && (courseCode || (isElectiveLesson && electiveLessonId))) {
      fetchWeeklyNotes();
    }
  }, [programId, courseCode, isElectiveLesson, electiveLessonId, fetchWeeklyNotes]);

  useEffect(() => {
    if (programId && (courseCode || (isElectiveLesson && electiveLessonId))) {
      fetchWeeklyVideos();
    }
  }, [programId, courseCode, isElectiveLesson, electiveLessonId, fetchWeeklyVideos]);

  const openWeekModal = (weekNumber) => {
    setSelectedWeekNumber(weekNumber);
    setIsWeekModalOpen(true);
  };

  const closeWeekModal = () => {
    setIsWeekModalOpen(false);
    setSelectedWeekNumber(null);
  };

  const handleWordUpload = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (!file.name.endsWith('.docx')) {
      alert('Lütfen .docx dosyası seçin');
      return;
    }
    try {
      const form = new FormData();
      form.append('file', file);

      let uploadUrl;
      if (isElectiveLesson && electiveLessonId) {
        const lessonId = parseInt(electiveLessonId);
        if (isNaN(lessonId)) {
          alert('Geçersiz seçmeli ders ID');
          return;
        }
        uploadUrl = `${API_BASE_URL}/api/programs/${programId}/elective-lessons/${lessonId}/upload-word`;
      } else {
        uploadUrl = `${API_BASE_URL}/api/programs/${programId}/courses/${courseCode}/upload-word`;
      }

      const res = await fetch(uploadUrl, { method: 'POST', body: form });
      const json = await res.json();
      if (res.ok) {
        alert('Word dosyası başarıyla yüklendi');
        e.target.value = '';
        // Course details'i yeniden yükle
        fetchCourseDetails();
      } else {
        alert(`Hata: ${json && json.error ? json.error : 'Bilinmeyen hata'}`);
      }
    } catch (err) {
      console.error('Word yükleme hatası:', err);
      alert('Yükleme hatası');
    }
  };

  const handleVideoInputChange = (weekNumber, field, value) => {
    setVideoInputs(prev => ({
      ...prev,
      [weekNumber]: {
        ...(prev[weekNumber] || {}),
        [field]: value
      }
    }));
  };

  const handleAddVideoLink = async (weekNumber) => {
    const input = videoInputs[weekNumber] || {};
    const url = (input.url || '').trim();
    if (!url) {
      alert('Video linki zorunludur');
      return;
    }
    try {
      setVideoLoadingState(prev => ({ ...prev, [weekNumber]: true }));
      const response = await fetch(`${API_BASE_URL}/api/programs/${programId}/courses/${courseCode}/weekly-videos`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'User-Type': userType || 'admin'
        },
        body: JSON.stringify({
          week_number: weekNumber,
          title: (input.title || '').trim(),
          url
        })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        alert(data.error || 'Video linki eklenemedi');
        return;
      }
      setVideoInputs(prev => ({
        ...prev,
        [weekNumber]: { title: '', url: '' }
      }));
      await fetchWeeklyVideos();
      alert('Video linki başarıyla eklendi');
    } catch (err) {
      console.error('Video linki ekleme hatası:', err);
      alert('Video linki eklenirken bir hata oluştu');
    } finally {
      setVideoLoadingState(prev => ({ ...prev, [weekNumber]: false }));
    }
  };

  const handleDeleteVideoLink = async (weekNumber, videoId) => {
    const video = (weekVideos[weekNumber] || []).find(v => v.id === videoId);
    const videoTitle = video ? (video.title || video.url) : 'bu video linki';
    if (!window.confirm(`${videoTitle} linkini silmek istediğinize emin misiniz?`)) {
      return;
    }
    try {
      const response = await fetch(`${API_BASE_URL}/api/programs/${programId}/courses/${courseCode}/weekly-videos/${videoId}`, {
        method: 'DELETE',
        headers: {
          'User-Type': userType || 'admin'
        }
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        alert(data.error || 'Video linki silinemedi');
        return;
      }
      await fetchWeeklyVideos();
      alert('Video linki başarıyla silindi');
    } catch (err) {
      console.error('Video linki silme hatası:', err);
      alert('Video linki silinirken bir hata oluştu');
    }
  };

  // PDF oluşturma fonksiyonu - Metin tabanlı (Kopyalanabilir metin için)
  const generatePDF = async () => {
    // Kullanıcıya bilgi ver
    const loadingMsg = document.createElement('div');
    loadingMsg.style.cssText = 'position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%); background: rgba(0,0,0,0.8); color: white; padding: 20px 40px; border-radius: 10px; z-index: 10000; font-size: 16px;';
    loadingMsg.textContent = 'PDF oluşturuluyor, lütfen bekleyin...';
    document.body.appendChild(loadingMsg);

    try {
      const { jsPDF } = await import('jspdf');
      const pdf = new jsPDF('p', 'mm', 'a4');

      // OpenSans fontunu yükle - ZORUNLU
      console.log('OpenSans fontu yükleniyor...');
      const openSansReady = await loadOpenSansFont(pdf);
      console.log('OpenSans fontu yükleme sonucu:', openSansReady);

      if (!openSansReady) {
        console.warn('OpenSans fontu yüklenemedi, alternatif yöntemler deneniyor...');
      }

      // Varsayılan font olarak OpenSans kullan - yüklenmediyse bile Türkçe karakterleri koru
      const targetFont = openSansReady ? 'OpenSans' : 'helvetica';

      // Font ayarlama helper fonksiyonu - OpenSans'ı zorla kullan
      const setFontSafe = (fontName = targetFont, style = 'normal', size = null) => {
        try {
          // Eğer OpenSans yüklendiyse, her zaman OpenSans kullan
          if (openSansReady) {
            if (size !== null) {
              pdf.setFontSize(size);
            }
            pdf.setFont('OpenSans', style);
          } else {
            // OpenSans yüklenmediyse helvetica kullan
            if (size !== null) {
              pdf.setFontSize(size);
            }
            pdf.setFont('helvetica', style);
          }
        } catch (e) {
          console.warn('Font ayarlama hatası:', e);
          try {
            if (size !== null) {
              pdf.setFontSize(size);
            }
            pdf.setFont('helvetica', style);
          } catch (e2) {
            console.error('Font ayarlama tamamen başarısız:', e2);
          }
        }
      };

      // Varsayılan fontu ayarla ve test et
      setFontSafe(targetFont, 'normal', 10);

      // Font testi - Türkçe karakterleri test et
      try {
        const testText = 'İğüşöç';
        pdf.text(testText, -100, -100); // Görünmez yerde test
        console.log('Font testi başarılı');
      } catch (e) {
        console.warn('Font testi hatası:', e);
      }

      // UTF-8 encoding için
      pdf.internal.pageSize.width = 210;
      pdf.internal.pageSize.height = 297;

      // PDF güvenlik ayarları - kopyalamayı engelle
      pdf.setProperties({
        title: `${courseDetails.course_code} - ${courseDetails.course_name}`,
        subject: 'Ders Bilgi Formu',
        author: 'Kocaeli Sağlık ve Teknoloji Üniversitesi',
        creator: 'EBS Sistemi',
        producer: 'jsPDF'
      });

      // Metni temizle - Türkçe karakterleri koru
      const cleanText = (text) => {
        if (!text) return '';
        return String(text)
          .replace(/\s+/g, ' ') // Fazla boşlukları tek boşluk yap
          .replace(/\t/g, ' ') // Tab'ları boşluk yap
          .replace(/\n/g, ' ') // Satır sonlarını boşluk yap
          .replace(/\r/g, '') // Carriage return'ları sil
          .trim(); // Başta ve sonda boşluk temizle
      };

      // Türkçe karakterleri koruyarak temizle - OpenSans ile çalışır
      const encodeTurkishText = (text) => {
        if (!text) return '';
        // Türkçe karakterleri her zaman koru, sadece temizle
        // normalize('NFC') Unicode normalizasyonu yapar
        let cleaned = cleanText(String(text));

        // Her zaman Unicode string kullan - Türkçe karakterleri koru
        return cleaned.normalize('NFC');
      };

      // Sayfa ayarları
      const pageWidth = 210;
      const pageHeight = 297;
      const margin = 20;
      const contentWidth = pageWidth - (2 * margin);
      let yPosition = margin;
      const lineHeight = 6;
      const titleHeight = 8;
      const sectionHeight = 10;

      // Header section - University logo and info
      setFontSafe(targetFont, 'bold', 14);
      pdf.text(encodeTurkishText('KOCAELİ SAĞLIK VE TEKNOLOJİ ÜNİVERSİTESİ'), pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 8;

      setFontSafe(targetFont, 'normal', 10);
      pdf.text(encodeTurkishText('Öğrenme Çıktıları Uyum Analizi'), pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 15;

      // Course title
      setFontSafe(targetFont, 'bold', 16);
      const courseTitle = `${encodeTurkishText(courseDetails.course_code)} - ${encodeTurkishText(courseDetails.course_name)}`;
      pdf.text(courseTitle, pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 10;

      // Program name
      setFontSafe(targetFont, 'normal', 12);
      pdf.text(`Program: ${encodeTurkishText(courseDetails.program_name || 'Program Adı')}`, pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 15;

      // Ders Detayları Tablosu
      if (courseDetails) {
        setFontSafe(targetFont, 'bold', 12);
        pdf.text(encodeTurkishText('DERS DETAYLARI'), margin, yPosition);
        yPosition += titleHeight + 2;

        const courseDetailFields = [
          { label: 'Ders Kodu', value: courseDetails.course_code },
          { label: 'Ders Adı', value: courseDetails.course_name },
          { label: 'Enstitü/Fakülte/MYO', value: courseDetails.enstitu_fakulte || courseDetails.program_name || '' },
          { label: 'Program', value: courseDetails.program_name },
          { label: 'Öğretim Düzeyi', value: courseDetails.ogretim_duzeyi || '' },
          { label: 'Öğretim Türü', value: courseDetails.ogretim_turu || '' },
          { label: 'Dönem', value: courseDetails.donem },
          { label: 'Kredi', value: courseDetails.kredi },
          { label: 'AKTS', value: courseDetails.akts },
          { label: 'Ders Türü', value: courseDetails.course_type },
          { label: 'Ön Koşul', value: courseDetails.on_kosul_dersleri || 'Yok' },
          { label: 'Dil', value: courseDetails.course_language },
          { label: 'Teorik', value: courseDetails.teorik !== null && courseDetails.teorik !== undefined ? courseDetails.teorik : '' },
          { label: 'Uygulama', value: courseDetails.uygulama !== null && courseDetails.uygulama !== undefined ? courseDetails.uygulama : '' },
          { label: 'Laboratuvar', value: courseDetails.laboratuvar !== null && courseDetails.laboratuvar !== undefined ? courseDetails.laboratuvar : '' }
        ];

        courseDetailFields.forEach((field, index) => {
          if (yPosition > pageHeight - 25) {
            pdf.addPage();
            yPosition = margin;
          }

          const bgColor = index % 2 === 0 ? [248, 249, 250] : [255, 255, 255];
          pdf.setFillColor(...bgColor);

          // Label ve value için satır sayısını hesapla
          setFontSafe(targetFont, 'bold', 9);
          const labelText = encodeTurkishText(field.label);
          const labelMaxWidth = contentWidth * 0.35 - 6;
          const labelLines = pdf.splitTextToSize(labelText, labelMaxWidth);

          setFontSafe(targetFont, 'normal', 9);
          const valueText = encodeTurkishText(String(field.value || ''));
          const valueMaxWidth = contentWidth * 0.65 - 6;
          const valueLines = pdf.splitTextToSize(valueText, valueMaxWidth);

          // Dinamik row height hesapla
          const maxLines = Math.max(labelLines.length, valueLines.length);
          const rowHeight = Math.max(10, 6 + (Math.min(maxLines, 3) * 4));

          pdf.rect(margin, yPosition, contentWidth, rowHeight, 'F');

          // Draw borders
          pdf.setDrawColor(200, 200, 200);
          pdf.setLineWidth(0.1);
          pdf.rect(margin, yPosition, contentWidth * 0.35, rowHeight);
          pdf.rect(margin + contentWidth * 0.35, yPosition, contentWidth * 0.65, rowHeight);

          // Label - Türkçe karakterleri koru
          setFontSafe(targetFont, 'bold', 9);
          let labelY = yPosition + 6;
          labelLines.slice(0, 3).forEach((line) => {
            pdf.text(line, margin + 3, labelY);
            labelY += 4;
          });

          // Value - Türkçe karakterleri koru, uzun metinleri böl
          setFontSafe(targetFont, 'normal', 9);
          let valueY = yPosition + 6;
          valueLines.slice(0, 3).forEach((line) => {
            pdf.text(line, margin + contentWidth * 0.35 + 3, valueY);
            valueY += 4;
          });

          yPosition += rowHeight;
        });
        yPosition += 10;
      }

      // Ders Açıklaması ve Amaçları
      if (courseDetails.content || courseDetails.aim) {
        if (yPosition > pageHeight - 50) {
          pdf.addPage();
          yPosition = margin;
        }

        if (courseDetails.content) {
          setFontSafe(targetFont, 'bold', 12);
          pdf.text(encodeTurkishText('DERS AÇIKLAMASI'), margin, yPosition);
          yPosition += titleHeight;

          setFontSafe(targetFont, 'normal', 9);
          const descLines = pdf.splitTextToSize(encodeTurkishText(courseDetails.content), contentWidth);
          descLines.forEach(line => {
            if (yPosition > pageHeight - 15) {
              pdf.addPage();
              yPosition = margin;
            }
            pdf.text(line, margin, yPosition);
            yPosition += 4;
          });
          yPosition += 10;
        }

        if (courseDetails.aim) {
          setFontSafe(targetFont, 'bold', 12);
          pdf.text(encodeTurkishText('DERS AMAÇLARI'), margin, yPosition);
          yPosition += titleHeight;

          setFontSafe(targetFont, 'normal', 9);
          const objLines = pdf.splitTextToSize(encodeTurkishText(courseDetails.aim), contentWidth);
          objLines.forEach(line => {
            if (yPosition > pageHeight - 15) {
              pdf.addPage();
              yPosition = margin;
            }
            pdf.text(line, margin, yPosition);
            yPosition += 4;
          });
          yPosition += 10;
        }
      }

      // Ders Kaynakları
      if (courseResources && (courseResources.main_resources || courseResources.supplementary_resources)) {
        if (yPosition > pageHeight - 50) {
          pdf.addPage();
          yPosition = margin;
        }

        setFontSafe(targetFont, 'bold', 12);
        pdf.text(encodeTurkishText('DERS KAYNAKLARI'), margin, yPosition);
        yPosition += titleHeight;

        if (courseResources.main_resources) {
          setFontSafe(targetFont, 'bold', 10);
          pdf.text(encodeTurkishText('Ana Kaynaklar:'), margin, yPosition);
          yPosition += 6;

          setFontSafe(targetFont, 'normal', 9);
          const mainLines = pdf.splitTextToSize(encodeTurkishText(courseResources.main_resources), contentWidth - 10);
          mainLines.forEach(line => {
            if (yPosition > pageHeight - 15) {
              pdf.addPage();
              yPosition = margin;
            }
            pdf.text(line, margin + 5, yPosition);
            yPosition += 4;
          });
          yPosition += 8;
        }

        if (courseResources.supplementary_resources) {
          setFontSafe(targetFont, 'bold', 10);
          pdf.text(encodeTurkishText('Yardımcı Kaynaklar:'), margin, yPosition);
          yPosition += 6;

          setFontSafe(targetFont, 'normal', 9);
          const suppLines = pdf.splitTextToSize(encodeTurkishText(courseResources.supplementary_resources), contentWidth - 10);
          suppLines.forEach(line => {
            if (yPosition > pageHeight - 15) {
              pdf.addPage();
              yPosition = margin;
            }
            pdf.text(line, margin + 5, yPosition);
            yPosition += 4;
          });
          yPosition += 8;
        }
        yPosition += 10;
      }

      // Learning Outcomes and Program Outcomes Mapping Table
      setFontSafe(targetFont, 'bold', 12);
      pdf.text(encodeTurkishText('Öğrenme Çıktıları ve Program Çıktıları Eşleştirmeleri'), pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 15;

      if (learningProgramMapping && learningProgramMapping.length > 0) {
        // Table header
        const colWidth = contentWidth / 3;
        pdf.setFillColor(59, 89, 152); // Blue header
        pdf.rect(margin, yPosition, colWidth, 10, 'F');
        pdf.rect(margin + colWidth, yPosition, colWidth * 2, 10, 'F');

        pdf.setTextColor(255, 255, 255);
        setFontSafe(targetFont, 'bold', 10);
        pdf.text(encodeTurkishText('Öğrenme Çıktısı'), margin + colWidth / 2, yPosition + 6, { align: 'center' });
        pdf.text(encodeTurkishText('Eşleşen Program Çıktıları'), margin + colWidth + colWidth, yPosition + 6, { align: 'center' });
        yPosition += 10;

        // Table content
        pdf.setTextColor(0, 0, 0);
        setFontSafe(targetFont, 'normal', 9);

        learningProgramMapping.forEach((mapping, index) => {
          if (yPosition > pageHeight - 30) {
            pdf.addPage();
            yPosition = margin;

            // Repeat header on new page
            pdf.setFillColor(59, 89, 152);
            pdf.rect(margin, yPosition, colWidth, 10, 'F');
            pdf.rect(margin + colWidth, yPosition, colWidth * 2, 10, 'F');

            pdf.setTextColor(255, 255, 255);
            setFontSafe(targetFont, 'bold', 10);
            pdf.text(encodeTurkishText('Öğrenme Çıktısı'), margin + colWidth / 2, yPosition + 6, { align: 'center' });
            pdf.text(encodeTurkishText('Eşleşen Program Çıktıları'), margin + colWidth + colWidth, yPosition + 6, { align: 'center' });
            yPosition += 10;

            pdf.setTextColor(0, 0, 0);
            setFontSafe(targetFont, 'normal', 9);
          }

          const bgColor = index % 2 === 0 ? [248, 249, 250] : [255, 255, 255];
          const rowHeight = Math.max(15, Math.ceil(mapping.learning_outcome_name.length / 40) * 5);

          pdf.setFillColor(...bgColor);
          pdf.rect(margin, yPosition, contentWidth, rowHeight, 'F');

          // Draw borders
          pdf.setDrawColor(200, 200, 200);
          pdf.rect(margin, yPosition, colWidth, rowHeight);
          pdf.rect(margin + colWidth, yPosition, colWidth * 2, rowHeight);

          // Learning outcome number and name
          setFontSafe(targetFont, 'bold', 9);
          pdf.text(`ÖÇ${mapping.learning_outcome_number}`, margin + 5, yPosition + 8);
          setFontSafe(targetFont, 'normal', 9);
          const loLines = pdf.splitTextToSize(encodeTurkishText(mapping.learning_outcome_name), colWidth - 10);
          let lineY = yPosition + 8;
          loLines.slice(0, 2).forEach(line => { // Limit to 2 lines
            pdf.text(line, margin + 5, lineY);
            lineY += 4;
          });

          // Program outcome numbers
          setFontSafe(targetFont, 'bold', 9);
          pdf.text(encodeTurkishText(mapping.program_outcome_numbers || '-'), margin + colWidth + colWidth, yPosition + rowHeight / 2, { align: 'center' });

          yPosition += rowHeight;
        });

        yPosition += 10;
      } else {
        setFontSafe(targetFont, 'normal', 9);
        pdf.text(encodeTurkishText('Henüz öğrenme çıktıları ve program çıktıları eşleştirmesi yapılmamış.'), margin, yPosition);
        yPosition += 15;
      }

      // Weekly Topics Section
      if (weeklyTopics.length > 0) {
        // Check if we need a new page
        if (yPosition > pageHeight - 80) {
          pdf.addPage();
          yPosition = margin;
        }

        setFontSafe(targetFont, 'bold', 12);
        pdf.text(encodeTurkishText('Haftalık Ders Konuları'), pageWidth / 2, yPosition, { align: 'center' });
        yPosition += 15;

        // Create weekly topics table
        const colWidth = contentWidth / 2;

        // Table header
        pdf.setFillColor(59, 89, 152);
        pdf.rect(margin, yPosition, colWidth, 10, 'F');
        pdf.rect(margin + colWidth, yPosition, colWidth, 10, 'F');

        pdf.setTextColor(255, 255, 255);
        setFontSafe(targetFont, 'bold', 10);
        pdf.text(encodeTurkishText('Hafta'), margin + colWidth / 2, yPosition + 6, { align: 'center' });
        pdf.text(encodeTurkishText('Ders Konuları'), margin + colWidth + colWidth / 2, yPosition + 6, { align: 'center' });
        yPosition += 10;

        // Table content
        pdf.setTextColor(0, 0, 0);
        setFontSafe(targetFont, 'normal', 9);

        weeklyTopics.forEach((topic, index) => {
          if (yPosition > pageHeight - 30) {
            pdf.addPage();
            yPosition = margin;

            // Repeat header on new page
            pdf.setFillColor(59, 89, 152);
            pdf.rect(margin, yPosition, colWidth, 10, 'F');
            pdf.rect(margin + colWidth, yPosition, colWidth, 10, 'F');

            pdf.setTextColor(255, 255, 255);
            setFontSafe(targetFont, 'bold', 10);
            pdf.text(encodeTurkishText('Hafta'), margin + colWidth / 2, yPosition + 6, { align: 'center' });
            pdf.text(encodeTurkishText('Ders Konuları'), margin + colWidth + colWidth / 2, yPosition + 6, { align: 'center' });
            yPosition += 10;

            pdf.setTextColor(0, 0, 0);
            setFontSafe(targetFont, 'normal', 9);
          }

          const bgColor = index % 2 === 0 ? [248, 249, 250] : [255, 255, 255];
          const topicText = encodeTurkishText(topic.topic);
          const rowHeight = Math.max(12, Math.ceil(topicText.length / 50) * 6);

          pdf.setFillColor(...bgColor);
          pdf.rect(margin, yPosition, contentWidth, rowHeight, 'F');

          // Draw borders
          pdf.setDrawColor(200, 200, 200);
          pdf.rect(margin, yPosition, colWidth, rowHeight);
          pdf.rect(margin + colWidth, yPosition, colWidth, rowHeight);

          // Week number
          setFontSafe(targetFont, 'bold', 9);
          pdf.text(`${topic.number}`, margin + colWidth / 2, yPosition + rowHeight / 2, { align: 'center' });

          // Topic content
          setFontSafe(targetFont, 'normal', 9);
          const topicLines = pdf.splitTextToSize(topicText, colWidth - 10);
          let lineY = yPosition + 6;
          topicLines.slice(0, Math.floor(rowHeight / 4)).forEach(line => {
            pdf.text(line, margin + colWidth + 5, lineY);
            lineY += 4;
          });

          yPosition += rowHeight;
        });

        yPosition += 10;
      } else {
        setFontSafe(targetFont, 'normal', 9);
        pdf.text(encodeTurkishText('Henüz haftalık ders konuları eklenmemiş.'), margin, yPosition);
        yPosition += 15;
      }

      // Öğrenme Çıktıları
      if (learningOutcomes.length > 0) {
        if (yPosition > pageHeight - 50) {
          pdf.addPage();
          yPosition = margin;
        }

        setFontSafe(targetFont, 'bold', 12);
        pdf.text(encodeTurkishText('ÖĞRENME ÇIKTILARI'), margin, yPosition);
        yPosition += titleHeight;

        // Tablo başlıkları
        pdf.setFillColor(240, 240, 240);
        pdf.rect(margin, yPosition, contentWidth, 8, 'F');
        setFontSafe(targetFont, 'bold', 9);
        pdf.text(encodeTurkishText('NO'), margin + 5, yPosition + 5);
        pdf.text(encodeTurkishText('ÖĞRENME ÇIKTISI'), margin + contentWidth * 0.15, yPosition + 5);
        yPosition += 8;

        // Tablo içeriği
        setFontSafe(targetFont, 'normal', 9);
        learningOutcomes.forEach((outcome, index) => {
          if (yPosition > pageHeight - 20) {
            pdf.addPage();
            yPosition = margin;
          }

          const bgColor = index % 2 === 0 ? [248, 249, 250] : [255, 255, 255];
          pdf.setFillColor(...bgColor);

          // outcome.name veya outcome.outcome alanını kullan
          const outcomeText = outcome.name || outcome.outcome || '';
          const descLines = pdf.splitTextToSize(encodeTurkishText(outcomeText), contentWidth * 0.8);
          const rowHeight = Math.max(8, descLines.length * 4 + 2);

          pdf.rect(margin, yPosition, contentWidth, rowHeight, 'F');

          // outcome.number veya index+1 kullan
          const outcomeNumber = outcome.number || (index + 1);
          pdf.text(String(outcomeNumber), margin + 5, yPosition + 5);

          let tempY = yPosition + 5;
          descLines.forEach(line => {
            if (tempY > pageHeight - 15) {
              pdf.addPage();
              tempY = margin + 5;
            }
            pdf.text(line, margin + contentWidth * 0.15, tempY);
            tempY += 4;
          });

          yPosition += rowHeight;
        });
        yPosition += 10;
      }

      // Program Çıktıları
      if (programOutcomes.length > 0) {
        if (yPosition > pageHeight - 50) {
          pdf.addPage();
          yPosition = margin;
        }

        setFontSafe(targetFont, 'bold', 12);
        pdf.text(encodeTurkishText('PROGRAM ÇIKTILARI'), margin, yPosition);
        yPosition += titleHeight;

        // Tablo başlıkları
        pdf.setFillColor(240, 240, 240);
        pdf.rect(margin, yPosition, contentWidth, 8, 'F');
        setFontSafe(targetFont, 'bold', 9);
        pdf.text(encodeTurkishText('NO'), margin + 5, yPosition + 5);
        pdf.text(encodeTurkishText('PROGRAM ÇIKTISI'), margin + contentWidth * 0.15, yPosition + 5);
        yPosition += 8;

        // Tablo içeriği
        setFontSafe(targetFont, 'normal', 9);
        programOutcomes.forEach((outcome, index) => {
          if (yPosition > pageHeight - 20) {
            pdf.addPage();
            yPosition = margin;
          }

          const bgColor = index % 2 === 0 ? [248, 249, 250] : [255, 255, 255];
          pdf.setFillColor(...bgColor);

          // outcome.name veya outcome.outcome alanını kullan
          const outcomeText = outcome.name || outcome.outcome || '';
          const descLines = pdf.splitTextToSize(encodeTurkishText(outcomeText), contentWidth * 0.8);
          const rowHeight = Math.max(8, descLines.length * 4 + 2);

          pdf.rect(margin, yPosition, contentWidth, rowHeight, 'F');

          // outcome.number veya index+1 kullan
          const outcomeNumber = outcome.number || (index + 1);
          pdf.text(String(outcomeNumber), margin + 5, yPosition + 5);

          let tempY = yPosition + 5;
          descLines.forEach(line => {
            if (tempY > pageHeight - 15) {
              pdf.addPage();
              tempY = margin + 5;
            }
            pdf.text(line, margin + contentWidth * 0.15, tempY);
            tempY += 4;
          });

          yPosition += rowHeight;
        });
        yPosition += 10;
      }

      // AKTS Etkinlikleri
      console.log('AKTS Activities Debug:', aktsActivities, 'Length:', aktsActivities.length);
      if (aktsActivities.length > 0) {
        if (yPosition > pageHeight - 50) {
          pdf.addPage();
          yPosition = margin;
        }

        setFontSafe(targetFont, 'bold', 12);
        pdf.text(encodeTurkishText('AKTS ETKİNLİKLERİ'), margin, yPosition);
        yPosition += titleHeight;

        // Tablo başlıkları
        pdf.setFillColor(240, 240, 240);
        pdf.rect(margin, yPosition, contentWidth, 8, 'F');
        setFontSafe(targetFont, 'bold', 8);
        pdf.text(encodeTurkishText('ETKİNLİK ADI'), margin + 5, yPosition + 5);
        pdf.text(encodeTurkishText('SAYI'), margin + contentWidth * 0.5, yPosition + 5);
        pdf.text(encodeTurkishText('SÜRE'), margin + contentWidth * 0.65, yPosition + 5);
        pdf.text(encodeTurkishText('TOPLAM'), margin + contentWidth * 0.8, yPosition + 5);
        yPosition += 8;

        // Tablo içeriği
        setFontSafe(targetFont, 'normal', 8);
        aktsActivities
          .filter(activity =>
            activity.activity_name &&
            activity.activity_name.toLowerCase() !== 'etkinlikler/faaliyet' &&
            activity.activity_name.toLowerCase() !== 'etkinlikler/ faaliyet' &&
            activity.activity_name.toLowerCase() !== 'etkinlikler/faaliyet ' &&
            activity.activity_name.toLowerCase() !== 'etkinlikler/ faaliyet '
          )
          .forEach((activity, index) => {
            if (yPosition > pageHeight - 20) {
              pdf.addPage();
              yPosition = margin;
            }

            const bgColor = index % 2 === 0 ? [248, 249, 250] : [255, 255, 255];
            pdf.setFillColor(...bgColor);
            pdf.rect(margin, yPosition, contentWidth, 6, 'F');

            pdf.text(encodeTurkishText(activity.activity_name), margin + 5, yPosition + 4);
            pdf.text(String(activity.count), margin + contentWidth * 0.5, yPosition + 4);
            pdf.text(String(activity.duration), margin + contentWidth * 0.65, yPosition + 4);
            pdf.text(String(activity.total_hours), margin + contentWidth * 0.8, yPosition + 4);
            yPosition += 6;
          });
        yPosition += 10;
      }

      // Sürdürülebilir Kalkınma Amaçları
      console.log('Sustainable Development Goals Debug:', sustainableDevelopmentGoals, 'Length:', sustainableDevelopmentGoals.length);
      if (sustainableDevelopmentGoals.length > 0) {
        if (yPosition > pageHeight - 50) {
          pdf.addPage();
          yPosition = margin;
        }

        setFontSafe(targetFont, 'bold', 12);
        pdf.text(encodeTurkishText('SÜRDÜRÜLEBİLİR KALKINMA AMAÇLARI'), margin, yPosition);
        yPosition += titleHeight;

        // Tablo başlıkları
        pdf.setFillColor(240, 240, 240);
        pdf.rect(margin, yPosition, contentWidth, 8, 'F');
        setFontSafe(targetFont, 'bold', 9);
        pdf.text(encodeTurkishText('NO'), margin + 5, yPosition + 5);
        pdf.text(encodeTurkishText('AMAÇ AÇIKLAMASI'), margin + contentWidth * 0.15, yPosition + 5);
        pdf.text(encodeTurkishText('KATKI DÜZEYİ'), margin + contentWidth * 0.8, yPosition + 5);
        yPosition += 8;

        // Tablo içeriği
        setFontSafe(targetFont, 'normal', 9);
        sustainableDevelopmentGoals.forEach((goal, index) => {
          if (yPosition > pageHeight - 20) {
            pdf.addPage();
            yPosition = margin;
          }

          const bgColor = index % 2 === 0 ? [248, 249, 250] : [255, 255, 255];
          pdf.setFillColor(...bgColor);

          // Uzun metin için satır bölme
          const descLines = pdf.splitTextToSize(encodeTurkishText(goal.goal_description), contentWidth * 0.6);
          const rowHeight = Math.max(8, descLines.length * 4 + 2);

          pdf.rect(margin, yPosition, contentWidth, rowHeight, 'F');

          pdf.text(String(goal.goal_number), margin + 5, yPosition + 5);

          let tempY = yPosition + 5;
          descLines.forEach(line => {
            if (tempY > pageHeight - 15) {
              pdf.addPage();
              tempY = margin + 5;
            }
            pdf.text(line, margin + contentWidth * 0.15, tempY);
            tempY += 4;
          });

          // Katkı düzeyleri
          if (goal.contribution_levels && goal.contribution_levels.length > 0) {
            pdf.text(goal.contribution_levels.join(', '), margin + contentWidth * 0.8 + 10, yPosition + 5);
          } else {
            pdf.text('-', margin + contentWidth * 0.8 + 10, yPosition + 5);
          }

          yPosition += rowHeight;
        });
        yPosition += 10;
      }

      // Footer
      if (yPosition > pageHeight - 30) {
        pdf.addPage();
        yPosition = margin;
      }

      // Değerlendirme Sistemi
      console.log('Evaluation System Debug:', evaluationSystem, 'Length:', evaluationSystem.length);
      if (evaluationSystem.length > 0) {
        if (yPosition > pageHeight - 50) {
          pdf.addPage();
          yPosition = margin;
        }

        setFontSafe(targetFont, 'bold', 12);
        pdf.text(encodeTurkishText('DEĞERLENDİRME SİSTEMİ'), margin, yPosition);
        yPosition += titleHeight;

        // Tablo başlıkları
        pdf.setFillColor(240, 240, 240);
        pdf.rect(margin, yPosition, contentWidth, 8, 'F');
        setFontSafe(targetFont, 'bold', 9);
        pdf.text(encodeTurkishText('ETKİNLİK ADI'), margin + 5, yPosition + 5);
        pdf.text(encodeTurkishText('SAYI'), margin + contentWidth * 0.6, yPosition + 5);
        pdf.text(encodeTurkishText('KATKI PAYI (%)'), margin + contentWidth * 0.8, yPosition + 5);
        yPosition += 8;

        // Tablo içeriği
        setFontSafe(targetFont, 'normal', 9);
        evaluationSystem.forEach((item, index) => {
          if (yPosition > pageHeight - 20) {
            pdf.addPage();
            yPosition = margin;
          }

          const bgColor = index % 2 === 0 ? [248, 249, 250] : [255, 255, 255];
          pdf.setFillColor(...bgColor);
          pdf.rect(margin, yPosition, contentWidth, 6, 'F');

          pdf.text(encodeTurkishText(item.activity_name), margin + 5, yPosition + 4);
          pdf.text(String(item.count), margin + contentWidth * 0.6, yPosition + 4);
          pdf.text(String(item.contribution_share), margin + contentWidth * 0.8, yPosition + 4);
          yPosition += 6;
        });
        yPosition += 10;
      }

      // Öğretim Elemanları
      console.log('Teaching Staff Debug:', teachingStaff, 'Length:', teachingStaff.length);
      if (teachingStaff.length > 0) {
        if (yPosition > pageHeight - 50) {
          pdf.addPage();
          yPosition = margin;
        }

        setFontSafe(targetFont, 'bold', 12);
        pdf.text(encodeTurkishText('ÖĞRETİM ELEMANLARI'), margin, yPosition);
        yPosition += titleHeight;

        // Tablo başlıkları
        pdf.setFillColor(240, 240, 240);
        pdf.rect(margin, yPosition, contentWidth, 8, 'F');
        setFontSafe(targetFont, 'bold', 8);
        pdf.text(encodeTurkishText('UNVAN'), margin + 2, yPosition + 5);
        pdf.text(encodeTurkishText('AD SOYAD'), margin + contentWidth * 0.2, yPosition + 5);
        pdf.text(encodeTurkishText('WEB ADRESİ'), margin + contentWidth * 0.45, yPosition + 5);
        pdf.text(encodeTurkishText('E-POSTA'), margin + contentWidth * 0.65, yPosition + 5);
        pdf.text(encodeTurkishText('GÖREVİ'), margin + contentWidth * 0.85, yPosition + 5);
        yPosition += 8;

        // Tablo içeriği
        setFontSafe(targetFont, 'normal', 8);
        teachingStaff.forEach((staff, index) => {
          if (yPosition > pageHeight - 20) {
            pdf.addPage();
            yPosition = margin;
          }

          const bgColor = index % 2 === 0 ? [248, 249, 250] : [255, 255, 255];
          pdf.setFillColor(...bgColor);
          pdf.rect(margin, yPosition, contentWidth, 6, 'F');

          pdf.text(encodeTurkishText(staff.title), margin + 2, yPosition + 4);
          pdf.text(encodeTurkishText(`${staff.first_name} ${staff.last_name}`), margin + contentWidth * 0.2, yPosition + 4);
          pdf.text(encodeTurkishText(staff.web_address || ''), margin + contentWidth * 0.45, yPosition + 4);
          pdf.text(encodeTurkishText(staff.email || ''), margin + contentWidth * 0.65, yPosition + 4);
          pdf.text(encodeTurkishText(staff.duty), margin + contentWidth * 0.85, yPosition + 4);
          yPosition += 6;
        });
        yPosition += 10;
      }

      // Program Öğrenme Çıktıları
      if (programLearningOutcomes.length > 0) {
        if (yPosition > pageHeight - 50) {
          pdf.addPage();
          yPosition = margin;
        }

        setFontSafe(targetFont, 'bold', 12);
        pdf.text(encodeTurkishText('PROGRAM ÖĞRENME ÇIKTILARI'), margin, yPosition);
        yPosition += titleHeight;

        // Tablo başlıkları
        pdf.setFillColor(240, 240, 240);
        pdf.rect(margin, yPosition, contentWidth, 8, 'F');
        setFontSafe(targetFont, 'bold', 9);
        pdf.text(encodeTurkishText('SIRA NO'), margin + 5, yPosition + 5);
        pdf.text(encodeTurkishText('PROGRAM ÖĞRENME ÇIKTILARI'), margin + contentWidth * 0.15, yPosition + 5);
        pdf.text(encodeTurkishText('KATKI DÜZEYİ'), margin + contentWidth * 0.8, yPosition + 5);
        yPosition += 8;

        // Tablo içeriği
        setFontSafe(targetFont, 'normal', 9);
        programLearningOutcomes.forEach((outcome, index) => {
          if (yPosition > pageHeight - 20) {
            pdf.addPage();
            yPosition = margin;
          }

          const bgColor = index % 2 === 0 ? [248, 249, 250] : [255, 255, 255];
          pdf.setFillColor(...bgColor);
          pdf.rect(margin, yPosition, contentWidth, 6, 'F');

          pdf.text(String(outcome.serial_number), margin + 5, yPosition + 4);

          // Uzun metin için satır bölme
          const descLines = pdf.splitTextToSize(encodeTurkishText(outcome.outcome_description), contentWidth * 0.6);
          let tempY = yPosition + 4;
          descLines.forEach(line => {
            if (tempY > pageHeight - 15) {
              pdf.addPage();
              tempY = margin + 4;
            }
            pdf.text(line, margin + contentWidth * 0.15, tempY);
            tempY += 4;
          });

          // Katkı düzeyleri
          if (outcome.contribution_level) {
            pdf.text(String(outcome.contribution_level), margin + contentWidth * 0.8 + 10, yPosition + 4);
          } else {
            pdf.text('-', margin + contentWidth * 0.8 + 10, yPosition + 4);
          }

          yPosition = Math.max(yPosition + 6, tempY + 2);
        });
        yPosition += 10;
      }

      setFontSafe(targetFont, 'italic', 8);
      pdf.text(encodeTurkishText('Bu belge Kocaeli Sağlık ve Teknoloji Üniversitesi tarafından hazırlanmıştır.'), margin, yPosition);
      yPosition += lineHeight;
      pdf.text(encodeTurkishText(`Oluşturulma Tarihi: ${new Date().toLocaleDateString('tr-TR')}`), margin, yPosition);

      // PDF'i indir - Dosya adında Türkçe karakterleri İngilizce'ye çevir
      const safeCourseCode = turkishToEnglish(courseDetails.course_code || 'Ders');
      const safeCourseName = turkishToEnglish(courseDetails.course_name || 'Detay');
      pdf.save(`${safeCourseCode}_${safeCourseName}.pdf`);
    } catch (error) {
      console.error('PDF indirme hatası:', error);
      alert('PDF indirme sırasında bir hata oluştu: ' + (error.message || error));
    } finally {
      // Loading mesajını kaldır
      if (document.body.contains(loadingMsg)) {
        document.body.removeChild(loadingMsg);
      }
    }
  };



  console.log('CourseDetails render state:', { loading, error, courseDetails });

  if (loading) {
    return <div className="course-details-loading">Course details yükleniyor...</div>;
  }

  if (error) {
    return <div className="course-details-error">Hata: {error}</div>;
  }

  if (!courseDetails) {
    return <div className="course-details-empty">Course details bulunamadı</div>;
  }

  return (
    <div className="course-details" ref={pageRef}>
      <div className="course-details-header">
        <button
          className="back-button"
          onClick={() => navigate(-1)}
        >
          ← Geri
        </button>
        <h2>Ders Detayları</h2>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          {userType === 'admin' && (
            <div style={{ position: 'relative' }}>
              <input
                type="file"
                accept=".docx"
                onChange={handleWordUpload}
                id="word-upload-input"
                style={{
                  position: 'absolute',
                  opacity: 0,
                  width: '100%',
                  height: '100%',
                  cursor: 'pointer',
                  zIndex: 2
                }}
              />
              <button
                className="upload-button"
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.2s',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  position: 'relative',
                  zIndex: 1
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#059669'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#10b981'}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
                İzlence Yükle
              </button>
            </div>
          )}
          <button
            className="pdf-button"
            onClick={generatePDF}
          >
            PDF İndir
          </button>
        </div>
      </div>

      {/* Ders Detayları Tablosu */}
      <table className="course-details-table">
        <tbody>
          <tr>
            <td className="table-label">Ders Kodu:</td>
            <td className="table-value">{courseDetails.course_code || 'Belirtilmemiş'}</td>
          </tr>
          <tr>
            <td className="table-label">Ders Adı:</td>
            <td className="table-value">{courseDetails.course_name || 'Belirtilmemiş'}</td>
          </tr>
          <tr>
            <td className="table-label">Enstitü/Fakülte/MYO:</td>
            <td className="table-value">{courseDetails.enstitu_fakulte || 'Belirtilmemiş'}</td>
          </tr>
          <tr>
            <td className="table-label">Program:</td>
            <td className="table-value">{courseDetails.cd_program || courseDetails.program_name || 'Belirtilmemiş'}</td>
          </tr>
          <tr>
            <td className="table-label">Öğretim Düzeyi:</td>
            <td className="table-value">{courseDetails.ogretim_duzeyi || 'Belirtilmemiş'}</td>
          </tr>
          <tr>
            <td className="table-label">Öğretim Türü:</td>
            <td className="table-value">{courseDetails.ogretim_turu || 'Belirtilmemiş'}</td>
          </tr>
          <tr>
            <td className="table-label">Dönem:</td>
            <td className="table-value">{courseDetails.donem || 'Belirtilmemiş'}</td>
          </tr>
          <tr>
            <td className="table-label">Teorik:</td>
            <td className="table-value">{courseDetails.teorik !== null && courseDetails.teorik !== undefined && courseDetails.teorik !== '' ? courseDetails.teorik : 'Belirtilmemiş'}</td>
          </tr>
          <tr>
            <td className="table-label">Uygulama:</td>
            <td className="table-value">{courseDetails.uygulama !== null && courseDetails.uygulama !== undefined && courseDetails.uygulama !== '' ? courseDetails.uygulama : 'Belirtilmemiş'}</td>
          </tr>
          <tr>
            <td className="table-label">Laboratuvar:</td>
            <td className="table-value">{courseDetails.laboratuvar !== null && courseDetails.laboratuvar !== undefined && courseDetails.laboratuvar !== '' ? courseDetails.laboratuvar : 'Belirtilmemiş'}</td>
          </tr>
          <tr>
            <td className="table-label">Kredi:</td>
            <td className="table-value">{courseDetails.kredi !== null && courseDetails.kredi !== undefined && courseDetails.kredi !== '' ? courseDetails.kredi : 'Belirtilmemiş'}</td>
          </tr>
          <tr>
            <td className="table-label">AKTS:</td>
            <td className="table-value">{courseDetails.akts !== null && courseDetails.akts !== undefined && courseDetails.akts !== '' ? courseDetails.akts : 'Belirtilmemiş'}</td>
          </tr>
          <tr>
            <td className="table-label">ECTS Kredisi:</td>
            <td className="table-value">{courseDetails.ects_credit !== null && courseDetails.ects_credit !== undefined && courseDetails.ects_credit !== '' ? courseDetails.ects_credit : 'Belirtilmemiş'}</td>
          </tr>
          <tr>
            <td className="table-label">Ders Tipi:</td>
            <td className="table-value">{courseDetails.course_type || courseDetails.cd_course_type || 'Belirtilmemiş'}</td>
          </tr>
          <tr>
            <td className="table-label">Katalog No:</td>
            <td className="table-value">{courseDetails.catalog_no || 'Belirtilmemiş'}</td>
          </tr>
          <tr>
            <td className="table-label">Ders Dili:</td>
            <td className="table-value">{courseDetails.course_language || 'Belirtilmemiş'}</td>
          </tr>
          <tr>
            <td className="table-label">Koordinatör:</td>
            <td className="table-value">{courseDetails.coordinator || 'Belirtilmemiş'}</td>
          </tr>
          <tr>
            <td className="table-label">Öğretim Üyesi:</td>
            <td className="table-value">{courseDetails.ogretim_uyesi || 'Belirtilmemiş'}</td>
          </tr>

        </tbody>
      </table>

      {/* Dersin Amacı */}
      <div className="course-details-section">
        <h3>Dersin Amacı</h3>
        <p>{courseDetails.aim || 'Amaç belirtilmemiş'}</p>
      </div>

      {/* Dersin İçeriği */}
      <div className="course-details-section">
        <h3>Dersin İçeriği</h3>
        <p>{courseDetails.content || 'İçerik belirtilmemiş'}</p>
      </div>

      {/* Ders Kaynakları */}
      <div className="course-details-section">
        <h3>Ders Kaynakları</h3>
        {courseResources && courseResources.ders_kaynaklari.length > 0 ? (
          <ul className="resources-list">
            {courseResources.ders_kaynaklari.map((resource, index) => (
              <li key={index} className="resource-item">{resource}</li>
            ))}
          </ul>
        ) : courseDetails.ders_kaynaklari ? (
          <p>{courseDetails.ders_kaynaklari}</p>
        ) : (
          <p>Belirtilmemiş</p>
        )}
      </div>

      {/* Önerilen Kaynaklar */}
      <div className="course-details-section">
        <h3>Önerilen Kaynaklar</h3>
        {courseResources && courseResources.onerilen_kaynaklar.length > 0 ? (
          <ul className="resources-list">
            {courseResources.onerilen_kaynaklar.map((resource, index) => (
              <li key={index} className="resource-item">{resource}</li>
            ))}
          </ul>
        ) : courseDetails.onerilen_kaynaklar ? (
          <p>{courseDetails.onerilen_kaynaklar}</p>
        ) : (
          <p>Belirtilmemiş</p>
        )}
      </div>

      {/* Diğer Bilgiler */}
      <div className="course-details-section">
        <h3>Diğer Bilgiler</h3>
        <table className="course-details-table">
          <tbody>
            <tr>
              <td className="table-label">Ön Koşul Dersleri:</td>
              <td className="table-value">{courseDetails.on_kosul_dersleri || 'Belirtilmemiş'}</td>
            </tr>
            <tr>
              <td className="table-label">Özel Koşul:</td>
              <td className="table-value">{courseDetails.ozel_kosul || 'Belirtilmemiş'}</td>
            </tr>
            <tr>
              <td className="table-label">Web Sitesi:</td>
              <td className="table-value">{courseDetails.web_sitesi || 'Belirtilmemiş'}</td>
            </tr>
            <tr>
              <td className="table-label">Etik Kurallar:</td>
              <td className="table-value">
                <a
                  href="https://kocaelisaglik.edu.tr/sinav-uygulama-kurallari/"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: '#3b82f6', textDecoration: 'underline' }}
                >
                  https://kocaelisaglik.edu.tr/sinav-uygulama-kurallari/
                </a>
              </td>
            </tr>
            <tr>
              <td className="table-label">Ölçme Değerlendirme Yöntemi:</td>
              <td className="table-value">{courseDetails.olcme_degerlendirme_yontemi || 'Belirtilmemiş'}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Öğrenme Çıktıları Tablosu */}
      <div className="course-details-section">
        <h3>Dersin Öğrenme Çıktıları</h3>
        {learningOutcomes.length > 0 ? (
          <table className="outcomes-table">
            <thead>
              <tr>
                <th>No</th>
                <th>Öğrenme Çıktısı</th>
              </tr>
            </thead>
            <tbody>
              {learningOutcomes.map((outcome, index) => (
                <tr key={index}>
                  <td className="outcome-number">{outcome.number}</td>
                  <td className="outcome-text">{outcome.name}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p>Belirtilmemiş</p>
        )}
      </div>

      {/* Haftalık Konular + Ders Notları */}
      <div className="course-details-section">
        <h3>Haftalık Konular</h3>
        {weeklyTopics.length > 0 ? (
          <table className="weekly-topics-table">
            <thead>
              <tr>
                <th>Hafta</th>
                <th>Konu</th>
                <th>Ön Hazırlık</th>
                <th>Metodoloji</th>
                <th>Ders Notları</th>
              </tr>
            </thead>
            <tbody>
              {weeklyTopics.map((topic, index) => {
                const weekNumber = topic.number;
                const normalizedWeekNumber = parseWeekValue(weekNumber);
                const weekNotesList = weekNotes[normalizedWeekNumber] || [];
                const weekVideosList = weekVideos[normalizedWeekNumber] || [];
                const videoInput = videoInputs[normalizedWeekNumber] || { title: '', url: '' };
                return (
                  <tr key={index}>
                    <td className="topic-week">{topic.number}. Hafta</td>
                    <td className="topic-text">{topic.topic}</td>
                    <td className="topic-preparation">{topic.preparation || topic.on_hazirlik || '-'}</td>
                    <td className="topic-methodology">{topic.methodology || topic.metodoloji || '-'}</td>
                    <td className="topic-notes">
                      <div className="week-notes-container">
                        {userType === 'admin' && (
                          <>
                            <input
                              type="file"
                              accept=".pdf,.doc,.docx,.ppt,.pptx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation"
                              multiple
                              onChange={(e) => {
                                const files = Array.from(e.target.files || []);
                                const allowedExtensions = ['pdf', 'doc', 'docx', 'ppt', 'pptx'];
                                const validFiles = files.filter(file => {
                                  const ext = (file.name.split('.').pop() || '').toLowerCase();
                                  return allowedExtensions.includes(ext);
                                });

                                if (validFiles.length === 0) {
                                  alert('Lütfen PDF, Word veya PowerPoint dosyaları yükleyin.');
                                  e.target.value = '';
                                  return;
                                }

                                let uploadsInBatch = 0;
                                const uploadOne = async (f) => {
                                  try {
                                    const maxSize = 15 * 1024 * 1024; // 15MB
                                    if (f.size > maxSize) {
                                      throw new Error(`Dosya boyutu 15MB'dan büyük olamaz. Mevcut boyut: ${(f.size / (1024 * 1024)).toFixed(2)}MB`);
                                    }

                                    const currentWeekNotes = weekNotes[normalizedWeekNumber] || [];
                                    if (currentWeekNotes.length + uploadsInBatch >= 3) {
                                      throw new Error(`Bu hafta için maksimum 3 dosya yüklenebilir. Mevcut dosya sayısı: ${currentWeekNotes.length}`);
                                    }

                                    const form = new FormData();
                                    form.append('faculty', courseDetails.enstitu_fakulte || 'Fakulte');
                                    form.append('department', courseDetails.program_name || 'Program');
                                    form.append('course', courseDetails.course_code || courseDetails.course_name || 'Ders');
                                    form.append('week', `${weekNumber}. Hafta`);
                                    form.append('file', f);
                                    form.append('userType', userType || 'admin');

                                    const res = await fetch(API_ENDPOINTS.DRIVE_UPLOAD_PDF, {
                                      method: 'POST',
                                      headers: {
                                        'User-Type': userType || 'admin'
                                      },
                                      body: form,
                                    });

                                    if (!res.ok) {
                                      const errorData = await res.json().catch(() => ({ error: 'Yükleme başarısız' }));
                                      throw new Error(errorData.error || 'Yükleme başarısız');
                                    }

                                    uploadsInBatch += 1;
                                    await fetchWeeklyNotes();
                                    alert('Dosya başarıyla yüklendi');
                                  } catch (err) {
                                    console.error('Dosya yükleme hatası:', err);
                                    alert(`Dosya yüklenemedi: ${err.message}`);
                                  }
                                };

                                (async () => {
                                  for (const f of validFiles) {
                                    // eslint-disable-next-line no-await-in-loop
                                    await uploadOne(f);
                                  }
                                })();

                                e.target.value = '';
                              }}
                              id={`week-notes-${weekNumber}`}
                              className="week-notes-input"
                              style={{ display: 'none' }}
                            />
                            <label
                              htmlFor={`week-notes-${weekNumber}`}
                              className="week-notes-upload-btn"
                            >
                              Dosya Yükle
                            </label>
                          </>
                        )}

                        <button
                          type="button"
                          className="week-notes-open-btn"
                          onClick={() => openWeekModal(normalizedWeekNumber)}
                        >
                          Ders Notları
                        </button>

                        {userType === 'admin' && (
                          <div className="week-video-form">
                            <input
                              type="text"
                              className="week-video-input"
                              placeholder="Video başlığı (opsiyonel)"
                              value={videoInput.title || ''}
                              onChange={(event) => handleVideoInputChange(normalizedWeekNumber, 'title', event.target.value)}
                            />
                            <div className="week-video-row">
                              <input
                                type="url"
                                className="week-video-input"
                                placeholder="https://..."
                                value={videoInput.url || ''}
                                onChange={(event) => handleVideoInputChange(normalizedWeekNumber, 'url', event.target.value)}
                              />
                              <button
                                type="button"
                                className="week-video-add-btn"
                                onClick={() => handleAddVideoLink(normalizedWeekNumber)}
                                disabled={videoLoadingState[normalizedWeekNumber]}
                              >
                                {videoLoadingState[normalizedWeekNumber] ? 'Ekleniyor...' : 'Video Linki Ekle'}
                              </button>
                            </div>
                          </div>
                        )}

                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <p>Belirtilmemiş</p>
        )}
      </div>

      {/* Program Çıktıları Tablosu */}
      {false && programOutcomes.length > 0 && (
        <div className="course-details-section">
          <h3>Program Çıktıları</h3>
          <table className="outcomes-table">
            <thead>
              <tr>
                <th>No</th>
                <th>Program Çıktısı</th>
              </tr>
            </thead>
            <tbody>
              {programOutcomes.map((outcome, index) => (
                <tr key={index}>
                  <td className="outcome-number">{outcome.number}</td>
                  <td className="outcome-text">{outcome.name}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {isWeekModalOpen && selectedWeekNumber !== null && (
        <div className="week-modal-overlay" onClick={closeWeekModal}>
          <div className="week-modal" onClick={(e) => e.stopPropagation()}>
            <div className="week-modal-header">
              <h4>{selectedWeekNumber}. Hafta - Ders Notları</h4>
              <button type="button" className="week-modal-close" onClick={closeWeekModal} aria-label="Kapat">×</button>
            </div>
            <div className="week-modal-content">
              <div className="week-modal-section">
                <h5>Dosyalar</h5>
                {(weekNotes[selectedWeekNumber] || []).length === 0 ? (
                  <p className="week-modal-empty">Bu hafta için dosya eklenmemiş.</p>
                ) : (
                  <ul className="week-modal-list">
                    {(weekNotes[selectedWeekNumber] || []).map((file) => {
                      // Seçmeli dersler için farklı endpoint'ler kullan
                      const lessonId = isElectiveLesson && electiveLessonId ? parseInt(electiveLessonId) : null;
                      const viewUrl = lessonId
                        ? `${API_BASE_URL}/api/programs/${programId}/elective-lessons/${lessonId}/weekly-notes/${file.id}/view`
                        : `${API_BASE_URL}/api/programs/${programId}/courses/${courseCode}/weekly-notes/${file.id}/view`;
                      const downloadUrl = lessonId
                        ? `${API_BASE_URL}/api/programs/${programId}/elective-lessons/${lessonId}/weekly-notes/${file.id}/download`
                        : `${API_BASE_URL}/api/programs/${programId}/courses/${courseCode}/weekly-notes/${file.id}/download`;
                      const deleteUrl = lessonId
                        ? `${API_BASE_URL}/api/programs/${programId}/elective-lessons/${lessonId}/weekly-notes/${file.id}`
                        : `${API_BASE_URL}/api/programs/${programId}/courses/${courseCode}/weekly-notes/${file.id}`;
                      return (
                        <li key={file.id}>
                          <span style={{ flex: 1, wordBreak: 'break-word' }}>{file.name}</span>
                          <div className="week-modal-actions">
                            <button
                              type="button"
                              className="week-modal-button"
                              onClick={() => window.open(viewUrl, '_blank')}
                            >
                              Görüntüle
                            </button>
                            <button
                              type="button"
                              className="week-modal-button"
                              onClick={() => window.open(downloadUrl, '_blank')}
                            >
                              İndir
                            </button>
                            {userType === 'admin' && (
                              <button
                                type="button"
                                className="week-modal-button danger"
                                onClick={async () => {
                                  if (!window.confirm(`${file.name} dosyasını silmek istediğinize emin misiniz?`)) {
                                    return;
                                  }
                                  try {
                                    const response = await fetch(
                                      deleteUrl,
                                      {
                                        method: 'DELETE',
                                        headers: {
                                          'User-Type': userType || 'admin'
                                        }
                                      }
                                    );
                                    if (response.ok) {
                                      await fetchWeeklyNotes();
                                      alert('Dosya başarıyla silindi');
                                    } else {
                                      const errorData = await response.json().catch(() => ({ error: 'Silme başarısız' }));
                                      alert(errorData.error || 'Silme başarısız');
                                    }
                                  } catch (err) {
                                    console.error('Dosya silme hatası:', err);
                                    alert('Dosya silinirken bir hata oluştu');
                                  }
                                }}
                              >
                                Sil
                              </button>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
              <div className="week-modal-section">
                <h5>Video Linkleri</h5>
                {(weekVideos[selectedWeekNumber] || []).length === 0 ? (
                  <p className="week-modal-empty">Bu hafta için video linki yok.</p>
                ) : (
                  <ul className="week-modal-list">
                    {(weekVideos[selectedWeekNumber] || []).map((video) => (
                      <li key={video.id}>
                        <a href={video.url} target="_blank" rel="noopener noreferrer" style={{ flex: 1, wordBreak: 'break-word' }}>
                          {video.title || video.url}
                        </a>
                        {userType === 'admin' && (
                          <div className="week-modal-actions">
                            <button
                              type="button"
                              className="week-modal-button danger"
                              onClick={() => handleDeleteVideoLink(selectedWeekNumber, video.id)}
                            >
                              Sil
                            </button>
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* AKTS Aktiviteleri Tablosu */}
      <div className="course-details-section">
        <h3>AKTS (Öğrenci İş Yükü) Tablosu</h3>
        {aktsActivities && aktsActivities.length > 0 ? (
          <table className="akts-table">
            <thead>
              <tr>
                <th>Etkinlikler/Faaliyet</th>
                <th>Sayı</th>
                <th>Süre (Saat)</th>
                <th>Toplam (İş Yükü)</th>
              </tr>
            </thead>
            <tbody>
              {aktsActivities
                .filter(activity =>
                  activity.activity_name &&
                  activity.activity_name.toLowerCase() !== 'etkinlikler/faaliyet' &&
                  activity.activity_name.toLowerCase() !== 'etkinlikler/ faaliyet' &&
                  activity.activity_name.toLowerCase() !== 'etkinlikler/faaliyet ' &&
                  activity.activity_name.toLowerCase() !== 'etkinlikler/ faaliyet '
                )
                .filter(activity =>
                  (activity.count && activity.count !== 0 && activity.count !== '0') ||
                  (activity.duration_hours && activity.duration_hours !== 0 && activity.duration_hours !== '0') ||
                  (activity.total_workload && activity.total_workload !== 0 && activity.total_workload !== '0')
                )
                .map((activity, index) => (
                  <tr key={index}>
                    <td className="activity-name">{activity.activity_name}</td>
                    <td className="activity-count">{activity.count || '-'}</td>
                    <td className="activity-duration">{activity.duration_hours || '-'}</td>
                    <td className="activity-total">{activity.total_workload || '-'}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        ) : (
          <p>Belirtilmemiş</p>
        )}

        {/* AKTS Özet Bilgileri */}
        {courseDetails && (() => {
          const totalWorkload = Number(courseDetails.total_workload) || 0;
          const totalWorkloadPer30 = Number(courseDetails.total_workload_per_30) || 0;
          const aktsCredit = Number(courseDetails.akts_credit) || 0;

          const hasValidData = totalWorkload > 0 || totalWorkloadPer30 > 0 || aktsCredit > 0;

          if (!hasValidData) return null;

          return (
            <div className="akts-summary">
              <h4>AKTS Özet Bilgileri</h4>
              <table className="akts-summary-table">
                <tbody>
                  {totalWorkload > 0 && (
                    <tr>
                      <td>Toplam İş Yükü (Saat)</td>
                      <td>{totalWorkload}</td>
                    </tr>
                  )}
                  {totalWorkloadPer30 > 0 && (
                    <tr>
                      <td>Toplam İş Yükü (Saat)/30 (s)</td>
                      <td>{totalWorkloadPer30}</td>
                    </tr>
                  )}
                  {aktsCredit > 0 && (
                    <tr>
                      <td>Dersin AKTS Kredisi</td>
                      <td>{aktsCredit}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          );
        })()}
      </div>

      {/* Değerlendirme Sistemi Tablosu */}
      <div className="course-details-section">
        <h3>Değerlendirme Sistemi</h3>
        {evaluationSystem && evaluationSystem.length > 0 ? (
          <table className="evaluation-table">
            <thead>
              <tr>
                <th>Etkinlik Adı</th>
                <th>Sayı</th>
                <th>Katkı Payı</th>
              </tr>
            </thead>
            <tbody>
              {evaluationSystem.map((item, index) => (
                <tr key={index}>
                  <td className="activity-name">{item.activity_name}</td>
                  <td className="activity-count">{item.count}</td>
                  <td className="contribution-share">{item.contribution_share}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p>Belirtilmemiş</p>
        )}
      </div>

      {/* Öğretim Eleman(ları) Tablosu */}
      <div className="course-details-section">
        <h3>Öğretim Eleman(ları)</h3>
        {teachingStaff && teachingStaff.length > 0 ? (
          <table className="teaching-staff-table">
            <thead>
              <tr>
                <th>Unvan</th>
                <th>Ad</th>
                <th>Soyadı</th>
                <th>Web Adresi</th>
                <th>E-Posta</th>
                <th>Görevi</th>
              </tr>
            </thead>
            <tbody>
              {teachingStaff.map((staff, index) => (
                <tr key={index}>
                  <td className="staff-title">{staff.title}</td>
                  <td className="staff-first-name">{staff.first_name}</td>
                  <td className="staff-last-name">{staff.last_name}</td>
                  <td className="staff-web-address">{staff.web_address}</td>
                  <td className="staff-email">{staff.email}</td>
                  <td className="staff-duty">{staff.duty}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p>Belirtilmemiş</p>
        )}
      </div>

      {/* Program Öğrenme Çıktıları Tablosu */}
      {programLearningOutcomes.length > 0 && (
        <div className="course-details-section">
          <h3>Program Öğrenme Çıktıları</h3>
          <table className="program-learning-outcomes-table">
            <thead>
              <tr>
                <th style={{ width: '80px', textAlign: 'center' }}>Sıra No</th>
                <th>Program Öğrenme Çıktıları</th>
                <th style={{ width: '200px', textAlign: 'center' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <span>Katkı Düzeyi</span>
                    <div style={{ display: 'flex', gap: '8px', fontSize: '12px', marginTop: '4px' }}>
                      <span style={{ width: '24px' }}>1</span>
                      <span style={{ width: '24px' }}>2</span>
                      <span style={{ width: '24px' }}>3</span>
                      <span style={{ width: '24px' }}>4</span>
                      <span style={{ width: '24px' }}>5</span>
                    </div>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {programLearningOutcomes.map((outcome, index) => (
                <tr key={index}>
                  <td style={{ textAlign: 'center' }}>{outcome.serial_number || index + 1}</td>
                  <td>{outcome.outcome_description}</td>
                  <td style={{ textAlign: 'center' }}>
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '8px' }}>
                      {[1, 2, 3, 4, 5].map((level) => {
                        const isSelected = outcome.contribution_level === level;
                        return (
                          <div
                            key={level}
                            style={{
                              width: '24px',
                              height: '24px',
                              border: '1px solid #ccc',
                              borderRadius: '4px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: isSelected ? '#0f172a' : 'white',
                              color: isSelected ? 'white' : 'transparent',
                              fontWeight: 'bold',
                              fontSize: '14px'
                            }}
                          >
                            {isSelected ? 'X' : ''}
                          </div>
                        );
                      })}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Program ve Öğrenme Çıktıları İlişkisi Matrisi */}
      {outcomesMatrix && outcomesMatrix.matrix && outcomesMatrix.matrix.length > 0 && outcomesMatrix.program_outcomes && outcomesMatrix.program_outcomes.length > 0 && (
        <div className="course-details-section">
          <h3>Program ve Öğrenme Çıktıları İlişkisi</h3>
          <div style={{ overflowX: 'auto', marginTop: '20px', maxWidth: '100%' }}>
            <table className="outcomes-matrix-table" style={{ width: 'auto', minWidth: '100%', borderCollapse: 'collapse', fontSize: '14px', tableLayout: 'auto' }}>
              <thead>
                <tr>
                  <th style={{ border: '1px solid #ddd', padding: '12px', backgroundColor: '#f5f5f5', textAlign: 'center', minWidth: '90px', position: 'sticky', left: 0, zIndex: 2, fontWeight: 'bold' }}></th>
                  {outcomesMatrix.program_outcomes.map((po, index) => (
                    <th key={po.id} style={{ border: '1px solid #ddd', padding: '12px', backgroundColor: '#f5f5f5', textAlign: 'center', minWidth: '70px', whiteSpace: 'nowrap' }}>
                      PÇ{po.number}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {outcomesMatrix.matrix.map((row, rowIndex) => (
                  <tr key={row.learning_outcome_id}>
                    <td style={{ border: '1px solid #ddd', padding: '12px', backgroundColor: '#f5f5f5', textAlign: 'center', fontWeight: 'bold', position: 'sticky', left: 0, zIndex: 1 }}>
                      DÖÇ{row.learning_outcome_number}
                    </td>
                    {outcomesMatrix.program_outcomes.map((po) => {
                      // PÇ number'ını key olarak kullan (ID değil)
                      const contributionValue = row.contributions[po.number.toString()];
                      const hasMapping = contributionValue === 'X' || (contributionValue !== null && contributionValue !== undefined);
                      return (
                        <td key={po.id} style={{ border: '1px solid #ddd', padding: '12px', textAlign: 'center', backgroundColor: hasMapping ? '#fff' : '#f9f9f9', fontWeight: hasMapping ? 'bold' : 'normal', fontSize: hasMapping ? '16px' : '14px', color: hasMapping ? '#2c3e50' : '#999' }}>
                          {hasMapping ? (contributionValue === 'X' ? 'X' : contributionValue) : ''}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ marginTop: '15px', fontSize: '12px', color: '#666' }}>
              <strong>Açıklama:</strong> X işareti, ilgili Ders Öğrenme Çıktısının (DÖÇ) ilgili Program Çıktısına (PÇ) katkı sağladığını gösterir.
            </div>
          </div>
        </div>
      )}

      {/* Sürdürülebilir Kalkınma Amaçları Tablosu */}
      <div className="course-details-section">
        <h3>Sürdürülebilir Kalkınma Amaçları</h3>
        {sustainableDevelopmentGoals && sustainableDevelopmentGoals.length > 0 ? (
          <table className="sdg-table">
            <thead>
              <tr>
                <th style={{ width: '80px', textAlign: 'center' }}>Sıra No</th>
                <th>Sürdürülebilir Kalkınma Amaçları</th>
                <th style={{ width: '200px', textAlign: 'center' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <span>Katkı Düzeyi</span>
                    <div style={{ display: 'flex', gap: '8px', fontSize: '12px', marginTop: '4px' }}>
                      <span style={{ width: '24px' }}>1</span>
                      <span style={{ width: '24px' }}>2</span>
                      <span style={{ width: '24px' }}>3</span>
                      <span style={{ width: '24px' }}>4</span>
                      <span style={{ width: '24px' }}>5</span>
                    </div>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {sustainableDevelopmentGoals
                .sort((a, b) => parseInt(a.goal_number) - parseInt(b.goal_number))
                .map((goal, index) => (
                  <tr key={index}>
                    <td style={{ textAlign: 'center', width: '40px' }}>{goal.goal_number}</td>
                    <td>{goal.goal_description}</td>
                    <td style={{ textAlign: 'center' }}>
                      <div style={{ display: 'flex', justifyContent: 'center', gap: '8px' }}>
                        {[1, 2, 3, 4, 5].map((level) => {
                          const isSelected = goal.contribution_levels && goal.contribution_levels.includes(level);
                          return (
                            <div
                              key={level}
                              style={{
                                width: '24px',
                                height: '24px',
                                border: '1px solid #ccc',
                                borderRadius: '4px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: isSelected ? '#0f172a' : 'white',
                                color: isSelected ? 'white' : 'transparent',
                                fontWeight: 'bold',
                                fontSize: '14px'
                              }}
                            >
                              {isSelected ? 'X' : ''}
                            </div>
                          );
                        })}
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        ) : (
          <p>Belirtilmemiş</p>
        )}
      </div>
    </div>
  );
};

export default CourseDetails;
